import React from 'react';

import sortBy from 'lodash/sortBy';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { useQueryGroupByIds } from '@lumapps/groups/hooks/useQueryGroupByIds';
import { Group } from '@lumapps/groups/types';
import { mdiAccountMultipleOutline } from '@lumapps/lumx/icons';
import { useTranslate } from '@lumapps/translations';

import { DrawerChipsSection } from '../DrawerChipsSection';
import { DrawerSectionProps } from '../DrawerSection';

export interface DrawerGroupsSectionProps extends DrawerSectionProps {
    /** list of Group ids if you do not have the names for each group */
    groupsIds?: string[];
    /** groups to directly display of you already have them loaded */
    groups?: Partial<Group>[];
    /** scope for tracking purposes */
    scope?: string;
}

const CLASSNAME = 'lumx-drawer-chips-section';
const SCOPE = 'drawer-chips-section';
/**
 * Displays a list of Groups inside the context of a Drawer
 * @family Drawer
 * @param DrawerGroupsSectionProps
 * @returns DrawerGroupsSection
 */
export const DrawerGroupsSection: React.FC<DrawerGroupsSectionProps> = ({
    groups: preloadedGroups,
    groupsIds,
    scope = SCOPE,
    ...props
}) => {
    const { element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope);
    const { translateKey } = useTranslate();

    const { items: groups, isLoading } = useQueryGroupByIds(
        { uid: groupsIds || [] },
        {
            enabled: !!groupsIds && groupsIds.length > 0,
        },
    );

    const groupsToDisplay = preloadedGroups || groups || [];

    return (
        <DrawerChipsSection
            icon={mdiAccountMultipleOutline}
            {...props}
            isLoading={preloadedGroups ? false : isLoading}
            items={sortBy(groupsToDisplay, (group) => group.name?.toLowerCase())}
            getItemId={(group) => group.id as string}
            getItemLabel={(group) => translateKey(group.name || '')}
            chipProps={{
                className: element('group'),
            }}
            chipWrapperProps={{
                ...get({ element: 'groups' }),
                className: element('groups'),
            }}
            scope={scope}
        />
    );
};
